import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Link,
} from '@spotoninc/nexus-react';
import {
  Box,
  Text,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Button as ChakraButton,
  keyframes,
  Flex,
  Center,
  InputGroup,
  InputLeftElement,
  IconButton,
} from '@chakra-ui/react';
import {
  SearchIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
} from '@spotoninc/nexus-web-assets';
import '../../styles/reskinned-exclusion-modal.css';

// Define the bounce animation
const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
  40% {transform: translateY(-10px);}
  60% {transform: translateY(-5px);}
`;

/**
 * Exclusion Wizard Modal Component - Combines both exclusion definition and account selection
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {Object} props.selectedExclusion - The exclusion being edited (null for add mode)
 * @param {string} props.exclusionName - The name of the exclusion
 * @param {Function} props.setExclusionName - Function to update the exclusion name
 * @param {Array} props.integrations - List of available integrations
 * @param {Object} props.accounts - Map of integration IDs to their accounts
 * @param {Array} props.selectedIntegrations - List of selected integration IDs
 * @param {Function} props.setSelectedIntegrations - Function to update selected integrations
 * @param {Object} props.selectedAccounts - Map of integration IDs to their selected accounts
 * @param {Function} props.setSelectedAccounts - Function to update selected accounts
 * @param {boolean} props.isSubmitting - Whether the form is currently submitting
 * @param {Function} props.handleSubmit - Function to handle form submission
 * @param {boolean} props.hasChanges - Whether any changes have been made to the exclusion
 */
const ExclusionWizardModal = ({
  isOpen,
  onClose,
  selectedExclusion,
  exclusionName,
  setExclusionName,
  integrations,
  accounts,
  selectedIntegrations,
  setSelectedIntegrations,
  selectedAccounts,
  setSelectedAccounts,
  isSubmitting,
  handleSubmit,
  hasChanges,
}) => {
  // State to track which view is currently active
  const [currentView, setCurrentView] = useState('exclusion'); // 'exclusion' or 'accounts'

  // State for accounts view
  const [currentIntegrationId, setCurrentIntegrationId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelected, setLocalSelected] = useState([]);
  const [expandedAccounts, setExpandedAccounts] = useState({});

  // State to track which "Add accounts" link should be animated
  const [highlightedIntegration, setHighlightedIntegration] = useState(null);

  // Custom close handler to reset modal state
  const handleClose = () => {
    // Reset to exclusion view
    setCurrentView('exclusion');
    // Reset other state
    setSearchTerm('');
    setCurrentIntegrationId(null);
    // Call the parent's onClose function
    onClose();
  };

  // Refs for scrolling to elements
  const tableRef = useRef(null);
  const linkRefs = useRef({});

  // Reset view when modal is opened
  useEffect(() => {
    if (isOpen) {
      setCurrentView('exclusion');
    }
  }, [isOpen]);

  // Listen for highlight-add-accounts event
  useEffect(() => {
    const handleHighlightEvent = event => {
      const { integrationId } = event.detail;
      setHighlightedIntegration(integrationId);

      // Scroll to the highlighted integration
      if (linkRefs.current[integrationId]) {
        linkRefs.current[integrationId].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      // Clear the highlight after animation
      setTimeout(() => {
        setHighlightedIntegration(null);
      }, 3000);
    };

    document.addEventListener('highlight-add-accounts', handleHighlightEvent);

    return () => {
      document.removeEventListener(
        'highlight-add-accounts',
        handleHighlightEvent
      );
    };
  }, []);

  // Function to determine accounts link text based on selection count
  const getAccountsLinkText = integrationId => {
    // Convert to string for consistent comparison
    const stringIntegrationId = String(integrationId);

    // Check if integration is selected and has accounts
    if (
      selectedIntegrations.some(id => String(id) === stringIntegrationId) &&
      selectedAccounts?.[stringIntegrationId]?.length > 0
    ) {
      const count = selectedAccounts[stringIntegrationId].length;
      return `${count} ${count === 1 ? 'Account' : 'Accounts'}`;
    }

    return 'Add accounts';
  };

  // Handle integration selection change
  const handleIntegrationChange = (integrationId, isChecked) => {
    // Convert to string for consistent comparison
    const stringIntegrationId = String(integrationId);

    if (isChecked) {
      // Allow selection if:
      // 1. The integration has accounts, OR
      // 2. The integration was previously in the assigned_integrations array (for editing existing exclusions)
      const hasAccounts =
        accounts[integrationId] && accounts[integrationId].length > 0;
      const wasInOriginalSelection =
        selectedExclusion &&
        selectedExclusion.assigned_integrations &&
        selectedExclusion.assigned_integrations.some(
          id => String(id) === stringIntegrationId
        );

      if (hasAccounts || wasInOriginalSelection) {
        // Add the integration to the selected list
        setSelectedIntegrations(prev => {
          // Check if it's already in the list to avoid duplicates
          if (!prev.some(id => String(id) === stringIntegrationId)) {
            return [...prev, stringIntegrationId];
          }
          return prev;
        });

        // If this integration was previously selected but has no accounts in the state,
        // we need to initialize an empty array for it
        if (!selectedAccounts[stringIntegrationId] && wasInOriginalSelection) {
          // Check if there's integration-specific account data
          const integrationKey = `assigned_plparent_${stringIntegrationId}`;
          if (selectedExclusion[integrationKey]) {
            // Use the integration-specific account list
            setSelectedAccounts(prev => ({
              ...prev,
              [stringIntegrationId]: selectedExclusion[integrationKey] || [],
            }));
          } else {
            // Fallback to the general assigned_items if integration-specific data is not available
            setSelectedAccounts(prev => ({
              ...prev,
              [stringIntegrationId]: selectedExclusion.assigned_items || [],
            }));
          }
        }

        // Immediately open the accounts view for this integration
        handleOpenAccountsView(integrationId);
      }
    } else if (!isChecked) {
      setSelectedIntegrations(prev =>
        prev.filter(id => String(id) !== stringIntegrationId)
      );

      // Also remove any selected accounts for this integration
      setSelectedAccounts(prev => {
        const updated = { ...prev };
        delete updated[stringIntegrationId];
        return updated;
      });
    }
  };

  // Handle opening accounts view
  const handleOpenAccountsView = integrationId => {
    setCurrentIntegrationId(String(integrationId));

    // Initialize local selected state with any previously selected accounts
    setLocalSelected(selectedAccounts[String(integrationId)] || []);

    // Initialize expanded state for accounts with children
    const initialExpandedState = {};
    if (accounts[integrationId]) {
      accounts[integrationId].forEach(account => {
        if (account.children?.length > 0) {
          initialExpandedState[account.pk] = false;
        }
      });
    }
    setExpandedAccounts(initialExpandedState);

    // Switch to accounts view
    setCurrentView('accounts');
  };

  // Handle back button from accounts view
  const handleBackFromAccountsView = () => {
    // Check if any accounts were selected for the current integration
    const hasSelectedAccounts = localSelected && localSelected.length > 0;

    // If no accounts were selected, uncheck the integration
    if (!hasSelectedAccounts) {
      // Remove the integration from the selected list
      setSelectedIntegrations(prev =>
        prev.filter(id => String(id) !== String(currentIntegrationId))
      );
    } else {
      // Update the selected accounts for this integration
      setSelectedAccounts(prev => ({
        ...prev,
        [String(currentIntegrationId)]: localSelected,
      }));
    }

    // Reset search term
    setSearchTerm('');

    // Switch back to exclusion view
    setCurrentView('exclusion');
  };

  // Handle save accounts and return to exclusion view
  const handleSaveAccounts = () => {
    // Update the selected accounts for this integration
    setSelectedAccounts(prev => ({
      ...prev,
      [String(currentIntegrationId)]: localSelected,
    }));

    // Make sure the integration is in the selected list if accounts are selected
    if (localSelected.length > 0) {
      setSelectedIntegrations(prev => {
        if (!prev.some(id => String(id) === String(currentIntegrationId))) {
          return [...prev, String(currentIntegrationId)];
        }
        return prev;
      });
    }

    // Reset search term
    setSearchTerm('');

    // Switch back to exclusion view
    setCurrentView('exclusion');
  };

  // Get all child IDs for an account (recursive)
  const getAllChildrenIds = account => {
    let ids = [];
    if (!account.children) return ids;

    account.children.forEach(child => {
      ids.push(child.pk);
      if (child.children?.length > 0) {
        ids = [...ids, ...getAllChildrenIds(child)];
      }
    });

    return ids;
  };

  // Handle account selection toggle
  const handleToggleAccount = (account, isChecked) => {
    let newSelected = [...localSelected];

    if (isChecked) {
      // Add this account if not already selected
      if (!newSelected.includes(account.pk)) {
        newSelected.push(account.pk);
      }

      // If this is a parent account, add all its children
      if (account.children?.length > 0) {
        const childrenIds = getAllChildrenIds(account);
        childrenIds.forEach(childId => {
          if (!newSelected.includes(childId)) {
            newSelected.push(childId);
          }
        });
      }
    } else {
      // Remove this account
      newSelected = newSelected.filter(id => id !== account.pk);

      // If this is a parent account, remove all its children
      if (account.children?.length > 0) {
        const childrenIds = getAllChildrenIds(account);
        newSelected = newSelected.filter(id => !childrenIds.includes(id));
      }
    }

    setLocalSelected(newSelected);
  };

  // Check if any child is selected
  const hasAnyChildSelected = account => {
    if (!account.children?.length) return false;
    const childrenIds = getAllChildrenIds(account);
    return childrenIds.some(id => localSelected.includes(id));
  };

  // Toggle expanded state for an account
  const toggleExpanded = accountId => {
    setExpandedAccounts(prev => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  // Handle row click for expanding/collapsing
  const handleRowClick = account => {
    if (account.children?.length > 0) {
      toggleExpanded(account.pk);
    }
  };

  // Get all account IDs from a list (recursive)
  const getAllAccountIds = accountsList => {
    let ids = [];
    if (!accountsList) return ids;

    accountsList.forEach(account => {
      ids.push(account.pk);
      if (account.children?.length > 0) {
        ids = [...ids, ...getAllAccountIds(account.children)];
      }
    });

    return ids;
  };

  // Handle select all toggle
  const handleSelectAll = isChecked => {
    if (isChecked) {
      const allAccountIds = getAllAccountIds(filteredAccounts);
      setLocalSelected(allAccountIds);
    } else {
      setLocalSelected([]);
    }
  };

  // Filter accounts based on search term
  const filterAccountsRecursively = (accountsList, term) => {
    if (!accountsList) return [];

    return accountsList.filter(account => {
      const matchesSearch = account.desc
        ?.toLowerCase()
        .includes(term.toLowerCase());

      // If this account has children, check if any of them match the search
      let childrenMatch = false;
      if (account.children?.length > 0) {
        const matchingChildren = filterAccountsRecursively(
          account.children,
          term
        );
        childrenMatch = matchingChildren.length > 0;
      }

      return matchesSearch || childrenMatch;
    });
  };

  const filteredAccounts =
    searchTerm && currentIntegrationId
      ? filterAccountsRecursively(
          accounts[currentIntegrationId] || [],
          searchTerm
        )
      : accounts[currentIntegrationId] || [];

  // Check if the select all checkbox should be checked
  const areAllSelected =
    filteredAccounts.length > 0 &&
    getAllAccountIds(filteredAccounts).every(id => localSelected.includes(id));

  // Recursively render account items
  const renderAccountItems = (accountsList, level = 0) => {
    if (!accountsList?.length) return null;

    return accountsList.map(account => {
      const hasChildren = account.children?.length > 0;
      const isExpanded = expandedAccounts[account.pk];
      const isSelected = localSelected.includes(account.pk);
      const someChildrenSelected = hasAnyChildSelected(account);
      const indentation = level * 3; // Reduced from 20px to 3px (approx 1/6th)

      return (
        <React.Fragment key={account.pk}>
          <Box
            _hover={{ bg: 'gray.50' }}
            onClick={() => handleRowClick(account)}
            cursor={hasChildren ? 'pointer' : 'default'}
          >
            <Flex alignItems="center">
              <Box minWidth={`${indentation}px`} />
              <Center minWidth="20px">
                {hasChildren ? (
                  isExpanded ? (
                    <ChevronDownIcon boxSize="12px" />
                  ) : (
                    <ChevronRightIcon boxSize="12px" />
                  )
                ) : null}
              </Center>
              <Checkbox
                checked={isSelected}
                indeterminate={
                  !isSelected && hasChildren && someChildrenSelected
                }
                label={account.desc}
                onChange={e => {
                  e.stopPropagation();
                  handleToggleAccount(account, e.target.checked);
                }}
              />
            </Flex>
          </Box>

          {/* Render children if expanded */}
          {hasChildren && isExpanded && (
            <Box>
              <Box pl={2} mt={0.5} position="relative" ml={indentation + 7}>
                {renderAccountItems(account.children, level + 1)}
              </Box>
            </Box>
          )}
        </React.Fragment>
      );
    });
  };

  // Get current integration name for display in the accounts view title
  const getCurrentIntegrationName = () => {
    if (!currentIntegrationId) return '';
    const integration = integrations.find(
      i => String(i.id) === String(currentIntegrationId)
    );
    return integration ? integration.app_name : '';
  };

  // Get the current integration name for display
  const integrationName = getCurrentIntegrationName();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        currentView === 'exclusion' ? (
          selectedExclusion ? (
            'Edit Exclusion'
          ) : (
            'Define Exclusion'
          )
        ) : (
          <Flex align="center" alignItems={'center'}>
            <IconButton
              variant="ghost"
              aria-label="Go back"
              onClick={handleBackFromAccountsView}
              size="sm"
              borderRadius="full"
              _hover={{ bg: '#eff5ff' }}
              w="44px"
              h="44px"
              mr={6}
            >
              <ArrowLeftIcon width="24px" height="24px" />
            </IconButton>
            <Text
              style={{ fontSize: '1.5rem' }}
              minWidth={{ base: '100%', md: '400px' }}
            >
              Exclude Accounts {integrationName ? `for ${integrationName}` : ''}
            </Text>
          </Flex>
        )
      }
      className="exclusion-wizard-modal"
    >
      {currentView === 'exclusion' ? (
        // Exclusion View
        <>
          <ModalBody>
            <VStack spacing={4} align="center" px={{ base: 0, md: 4 }}>
              <FormControl>
                <FormLabel style={{ fontSize: '0.875rem', fontWeight: 400 }}>
                  Exclusion Name*
                </FormLabel>
                <Input
                  value={exclusionName}
                  onChange={e => setExclusionName(e.target.value)}
                  placeholder="Enter exclusion name"
                />
                <Text fontSize="xs" color="#37456C" w="90%" ml={1}>
                  Only accounts from connected accounting systems are shown.
                </Text>
              </FormControl>

              <Box
                minWidth={{ base: '100%', md: '700px' }}
                px={{ base: 2, md: 6 }}
              >
                <Box
                  maxHeight={{ base: 'auto', md: '400px' }}
                  overflowY={{ base: 'visible', md: 'auto' }}
                  ref={tableRef}
                >
                  <Table>
                    <TableHead
                      position="sticky"
                      top={0}
                      zIndex={1}
                      background="white"
                    >
                      <TableRow variant="filled">
                        <TableHeaderCell>Integration Name</TableHeaderCell>
                        <TableHeaderCell
                          textAlign="center"
                          style={{ width: '200px' }}
                        >
                          Excluded Accounts
                        </TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {integrations && integrations.length > 0 ? (
                        (() => {
                          // When editing an existing exclusion, show all integrations that are either:
                          // 1. Already selected (in selectedIntegrations)
                          // 2. Have accounts available
                          const filteredIntegrations = integrations.filter(
                            integration => {
                              // Convert both values to strings to ensure consistent comparison
                              const isSelected = selectedIntegrations.some(
                                id => String(id) === String(integration.id)
                              );
                              const hasAccounts =
                                accounts[integration.id] &&
                                accounts[integration.id].length > 0;

                              return (
                                // Always include integrations that are already selected
                                isSelected ||
                                // For new integrations, only include those with accounts
                                hasAccounts
                              );
                            }
                          );

                          if (filteredIntegrations.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={2} textAlign="center">
                                  <Text py={4}>
                                    No integrations with accounts available
                                  </Text>
                                </TableCell>
                              </TableRow>
                            );
                          }
                          return filteredIntegrations.map(integration => {
                            const stringIntegrationId = String(integration.id);
                            const isSelected = selectedIntegrations.some(
                              id => String(id) === stringIntegrationId
                            );
                            const hasSelectedAccounts =
                              selectedAccounts[stringIntegrationId]?.length > 0;

                            return (
                              <TableRow
                                key={integration.id}
                                variant="bottom-divider"
                              >
                                <TableCell>
                                  <Box display="flex" alignItems="center">
                                    <Checkbox
                                      id={`integration-${integration.id}`}
                                      checked={isSelected}
                                      onChange={e =>
                                        handleIntegrationChange(
                                          integration.id,
                                          e.target.checked
                                        )
                                      }
                                      mr={3}
                                    />
                                    <Text>{integration.app_name}</Text>
                                  </Box>
                                </TableCell>
                                <TableCell textAlign="center" width="150px">
                                  <Box
                                    animation={
                                      highlightedIntegration === integration.id
                                        ? `${bounce} 1s ease infinite`
                                        : 'none'
                                    }
                                    display="inline-block"
                                  >
                                    <Link
                                      color={
                                        isSelected && !hasSelectedAccounts
                                          ? 'red.500'
                                          : 'blue.500'
                                      }
                                      onClick={() =>
                                        handleOpenAccountsView(integration.id)
                                      }
                                      isDisabled={
                                        !isSelected ||
                                        (!isSelected &&
                                          (!accounts[integration.id] ||
                                            accounts[integration.id].length ===
                                              0))
                                      }
                                      cursor="pointer"
                                      ref={el => {
                                        if (el) {
                                          linkRefs.current[integration.id] = el;
                                        }
                                      }}
                                      fontWeight={
                                        isSelected && !hasSelectedAccounts
                                          ? 'bold'
                                          : 'normal'
                                      }
                                    >
                                      {getAccountsLinkText(integration.id)}
                                    </Link>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          });
                        })()
                      ) : (
                        <TableRow>
                          <TableCell colSpan={2} textAlign="center">
                            <Text py={4}>No integrations available</Text>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="tonal" onClick={handleClose}>
              Cancel
            </Button>
            <ChakraButton
              h="44px"
              colorScheme="blue"
              bg={'#2d71e8'}
              _hover={{ bg: !hasChanges ? '#a0aec0' : '#2d71e8' }}
              onClick={handleSubmit}
              isLoading={isSubmitting}
              borderRadius={100}
              px={3}
              isDisabled={
                !exclusionName.trim() ||
                !selectedIntegrations.length ||
                // Check if any selected integration has no accounts
                selectedIntegrations.some(integId => {
                  const stringIntegId = String(integId);
                  return (
                    !selectedAccounts[stringIntegId] ||
                    selectedAccounts[stringIntegId].length === 0
                  );
                }) ||
                // Check if there are any changes
                !hasChanges
              }
              title={
                !exclusionName.trim()
                  ? 'Exclusion name cannot be empty'
                  : !selectedIntegrations.length
                    ? 'Please select at least one integration'
                    : selectedIntegrations.some(integId => {
                          const stringIntegId = String(integId);
                          return (
                            !selectedAccounts[stringIntegId] ||
                            selectedAccounts[stringIntegId].length === 0
                          );
                        })
                      ? 'Please add accounts for all selected integrations'
                      : ''
              }
              opacity={!hasChanges ? 0.7 : 1}
            >
              <Text px={3}>Save</Text>
            </ChakraButton>
          </ModalFooter>
        </>
      ) : (
        // Accounts View
        <>
          <ModalBody>
            <VStack spacing={4} align="stretch" width="100%">
              <FormControl>
                <FormLabel style={{ fontSize: '0.875rem', fontWeight: 400 }}>
                  Search Accounts
                </FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.300" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search accounts"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </FormControl>

              {/* Select All Checkbox */}
              {filteredAccounts.length > 0 && (
                <Flex align="center" mb={2}>
                  <Checkbox
                    checked={areAllSelected}
                    onChange={e => handleSelectAll(e.target.checked)}
                    mr={2}
                  />
                  <Text fontSize="sm" fontWeight="medium">
                    Select All
                  </Text>
                </Flex>
              )}

              <Box
                maxHeight="400px"
                overflowY="auto"
                borderRadius="md"
                bg="#F4F7FA"
              >
                <Box px={3}>
                  {/* Account List */}
                  {filteredAccounts.length > 0 ? (
                    renderAccountItems(filteredAccounts)
                  ) : (
                    <Box py={4} textAlign="center">
                      <Text>
                        {searchTerm
                          ? 'No accounts match your search'
                          : 'No accounts available'}
                      </Text>
                    </Box>
                  )}
                </Box>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="tonal" onClick={handleBackFromAccountsView}>
              Back
            </Button>
            <Button
              variant="filled"
              onClick={handleSaveAccounts}
              isDisabled={localSelected.length === 0}
              title={
                localSelected.length === 0
                  ? 'Please select at least one account'
                  : ''
              }
              colorScheme="blue"
            >
              Save
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default ExclusionWizardModal;
